import { TABLEPAI, MONTH_FORMAT } from "@/enums/pageEnum";
import { useState, useMemo, useRef, useCallback, useTransition } from "react";
import { useTranslation } from "react-i18next";
import { BasicTable, ExportFile, Status } from "@/components";
import dayjs from "@/utils/dayjsConfig";
import { editCheckerRemaker } from "@api";
import { useAppSelector } from "@/store";
import { Button, Modal, Input } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import clsx from "clsx";
import { parseQueryString, parseMonth, getTimeToString } from "@/utils/toolt";

export default function LMChecker() {
  const { t } = useTranslation();
  const currentDate = useRef(new Date());
  const darkMode = useAppSelector((state) => state.app.theme);
  const [refreshKey, setRefreshKey] = useState<number>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rowData, setRowData] = useState<any>();
  const [isPending, startTransition] = useTransition();
  const [remark, setRemark] = useState<string>("");

  const colRender = useCallback((col: any, record: any) => {
    if (col.successFormat === "Not Done")
      return (
        <Button
          type="link"
          className="font-medium px-0 h-3 text-[#FF0006] underline"
          onClick={() => {
            setRemark(col?.remarks);
            setRowData({ ...record, ...col });
            setIsModalOpen(true);
          }}
        >
          {col.dateFormat}
        </Button>
      );
    return (
      <span
        className={clsx(
          "font-medium",
          col.successFormat === "Scheduled"
            ? "text-[#0364FF]"
            : "text-[#008000]"
        )}
      >
        {col.dateFormat}
      </span>
    );
  }, []);

  const durationRender = useCallback((col: any) => {
    if (col.successFormat === "Scheduled") return "";
    return (
      <span
        className={clsx(
          "font-medium",
          col.duration >= 45 ? "text-[#008000]" : "text-[#FF0006]"
        )}
      >
        {col.durationFormat}
      </span>
    );
  }, []);

  const columns: any[] = useMemo(
    () => [
      { title: t("TC"), dataIndex: "liftTc" },
      { title: t("Pfx"), dataIndex: "liftPfx" },
      { title: t("block"), dataIndex: "liftBlk" },
      { title: t("address"), dataIndex: "buildingAddress" },
      { title: t("lift"), dataIndex: "lift" },
      { title: t("LIFT_COMPANY"), dataIndex: "liftCompany" },
      {
        title: t("firstMaint"),
        children: [
          {
            title: t("scheduleDate"),
            dataIndex: ["checkers", 0],
            align: "center",
            render: colRender,
          },
          {
            title: t("duration"),
            dataIndex: ["checkers", 0],
            align: "center",
            render: durationRender,
          },
          {
            title: t("status"),
            dataIndex: ["checkers", 0, "successFormat"],
            align: "center",
          },
        ],
      },
      {
        title: t("secondMaint"),
        children: [
          {
            title: t("scheduleDate"),
            dataIndex: ["checkers", 1],
            align: "center",
            render: colRender,
          },
          {
            title: t("duration"),
            dataIndex: ["checkers", 1],
            render: durationRender,
            align: "center",
          },
          {
            title: t("status"),
            dataIndex: ["checkers", 1, "successFormat"],
            align: "center",
          },
        ],
      },
    ],
    [t, colRender, durationRender]
  );

  const monthOptions = useMemo(() => {
    const currentYear = currentDate.current.getFullYear();
    const currentMonth = currentDate.current.getMonth();
    const previousMonth = new Date(currentYear, currentMonth - 1, 1);
    const nextMonth = new Date(currentYear, currentMonth + 1, 1);
    return [
      {
        value: dayjs(previousMonth).format(MONTH_FORMAT),
        label: dayjs(previousMonth).format("MMMM YYYY"),
      },
      {
        value: dayjs(currentDate.current).format(MONTH_FORMAT),
        label: dayjs(currentDate.current).format("MMMM YYYY"),
      },
      {
        value: dayjs(nextMonth).format(MONTH_FORMAT),
        label: dayjs(nextMonth).format("MMMM YYYY"),
      },
    ];
  }, []);

  const getModalPureData = useMemo(() => {
    return (
      <Modal
        open={isModalOpen}
        width={680}
        okButtonProps={{ loading: isPending }}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        destroyOnClose
        closeIcon={
          <CloseOutlined
            className="text-[#000000d9] dark:text-[#ffffff73]"
            style={{ fontSize: 24 }}
          />
        }
      >
        <div>
          <h4 className="text-[20px] font-medium mt-7 mb-3">{t("remarks")}</h4>
          <Input.TextArea
            rows={3}
            value={remark}
            maxLength={255}
            showCount
            onChange={(e) => setRemark(e.target.value)}
          />
          <div className="mt-5 mb-2 flex items-center justify-between">
            <h4 className="text-[20px] font-medium">
              {t("LMHistory")} {rowData?.liftName}
            </h4>
            <Button
              type="primary"
              disabled={!remark}
              onClick={() => {
                if (!remark) return;
                startTransition(() => {
                  editCheckerRemaker({ id: rowData?.id, remarks: remark }).then(
                    () => {
                      setIsModalOpen(false);
                      setRefreshKey(Math.random());
                    }
                  );
                });
              }}
            >
              {t("save")}
            </Button>
          </div>
          <BasicTable
            isShowCheckbox={false}
            columns={[
              {
                title: t("statusDate"),
                dataIndex: "timestamp",
                renderText: (text: number) => <>{getTimeToString(text)}</>,
              },
              { title: t("description"), dataIndex: "description" },
              {
                title: t("alarmStatus"),
                dataIndex: "alarmStatus",
                renderText: (_: string) => <Status status={_} />,
              },
            ]}
            isDefaultToolBar={false}
            isPagination={false}
            rowKey={(_: any) => _.timestamp + _.description}
            footer={<span>{t("totalRows")}: </span>}
            className="!p-0 min-h-[calc(250px)]"
            dataKey="data.body"
            requestUrl={`/api/lm-checker/${rowData?.id}/lm-alarm`}
          />
        </div>
      </Modal>
    );
  }, [isModalOpen, t, rowData, remark, isPending]);

  const getPathParams = useCallback(() => {
    return parseMonth(parseQueryString());
  }, []);

  return (
    <div className="p-4">
      {getModalPureData}
      <BasicTable
        columns={columns}
        bordered
        className={darkMode === "light" ? "lmCheckerCalss" : ""}
        filterTableFields={[
          {
            field: "month",
            component: "select",
            options: monthOptions,
            fieldProps: { style: { width: 150 }, allowClear: false },
          },
          {
            field: "tc",
            label: "TC",
            apiUrl: TABLEPAI.LIFT_PAGE,
            component: "apiSelect",
            dataKey: "data.body.content",
            isShowPagination: true,
            isUniqBy: "tc",
            fieldProps: {
              popupClassName: "lmTcSelectModal",
              fieldNames: { value: "tc", label: "tc" },
            },
          },
          {
            field: "buildingId",
            label: t("ward"),
            apiUrl: TABLEPAI.BUILD_PAGE,
            component: "apiSelect",
            dataKey: "data.body.content",
            isShowPagination: true,
            isUniqBy: "id",
            fieldProps: {
              popupClassName: "lmTcSelectModal",
              fieldNames: { value: "id", label: "ward" },
            },
          },
          {
            field: "liftGroup",
            label: t("liftGroup"),
            component: "select",
            options: [],
          },
        ]}
        key={refreshKey}
        rowKey="liftId"
        isDefaultToolBar={false}
        headerTitle={() => t("lmChecker")}
        tableHeader={() => (
          <ExportFile
            disabled={false}
            onClick={() => {
              const path = new URL(window.location.href);
              const downurl = new URLSearchParams(getPathParams()).toString();
              const downString = `${path.origin}/api/lm-checker/export-excel?${downurl}`;
              window.open(downString);
            }}
            className="mr-0"
          />
        )}
        dataKey="data.body"
        requestUrl={TABLEPAI.LM_CHECKER}
        isShowCheckbox={false}
        isPagination={false}
      />
    </div>
  );
}
